import React, { useState } from "react";
import useEscapeLayer from "../../useEscape";
import { nowLine } from "../zones.mjs";
import { firstName } from "../format.mjs";
import { readable } from "./leadModel.mjs";

/*
 * THE LEAD BLOCK'S SHARED PIECES, the drawn card grammar every variant is built
 * in (frames 1a, 1c, 1d, 1g, 1i): a small grey label left, meta right, an 18px
 * headline, its sub-line, then sections under a 1px rule. No action buttons: the
 * lead block states the facts the situation turns on, and Next owns every button
 * (the brief, "Who owns what"). The few controls a block does carry are the
 * brief's own exceptions, drawn as links: "Add" on an empty field the block is the
 * one home for (the fee, a churn's reason), Held / No-show and Add notes on a
 * meeting nobody recorded, and Skip this call on a reminder plan.
 *
 * Props (each piece documents its own below):
 *   LeadCard      { label, meta, className, children }
 *   Sec           { tight, children }: a section under the 1px rule
 *   ReasonAndTime { record, ops, mode }: the score's one line and their time now
 *   Words         { text, size, muted }: their words at reading size
 *   Proposed      { children }: something that does not exist yet, inert
 *   NextPointer   {}: "in Next ↑", for an owed row whose button lives in Next
 *   InlineAdd     { label, placeholder, multiline, onSave, hint }
 *   Fold          { title, sub, open, onToggle, children }
 */

/** The card: `label` left, `meta` right, then the body. */
export function LeadCard({ label, meta = null, children, className = "" }) {
  return (
    <section className={`rec-card rec-lead ${className}`.trim()} aria-label={label}>
      <div className="rec-card__head">
        <span className="rec-label">{label}</span>
        {meta && <span className="rec-meta">{meta}</span>}
      </div>
      {children}
    </section>
  );
}

/** A section under the card's 1px rule. */
export function Sec({ children, tight = false, className = "" }) {
  return <div className={`rec-lead__sec${tight ? " rec-lead__sec--tight" : ""} ${className}`.trim()}>{children}</div>;
}

/*
 * THE SCORE'S ONE LINE AND THEIR TIME NOW, the foot of a block whose score is
 * featured (frames 1c and 1g):
 *   mode "why"   "Why 80: B2B SaaS, 180 people, VP Marketing, Series B+ in the US."
 *   mode "fits"  "Score 48 · high. Fits on industry and size, funding unknown. Breakdown"
 * then "Dana’s time now: 07:20 in Boston (ET) · 14:20 yours". Either half is left
 * out when there is nothing to say (no scoring set up; a zone nobody knows), and
 * the whole section with them.
 */
export function ReasonAndTime({ record, ops, mode = "fits", person = null }) {
  const reason = <ScoreLine record={record} ops={ops} mode={mode} />;
  const time = <TimeNow record={record} person={person} />;
  const hasReason = !!(record.score && record.score.show);
  const hasTime = !!timeNowText(record, person);
  if (!hasReason && !hasTime) return null;
  return (
    <Sec>
      {hasReason && reason}
      {hasTime && time}
    </Sec>
  );
}

function ScoreLine({ record, ops, mode }) {
  const s = record.score;
  if (!s || !s.show) return null;
  if (s.blocked) return <span className="rec-lead__ink">{s.blockedLine}.</span>;
  const why = mode === "why" && s.reason;
  return (
    <span className="rec-lead__ink">
      <b>{why ? `Why ${s.score}:` : `Score ${s.label}.`}</b> {why ? `${s.reason}.` : s.fitsOn}{" "}
      {mode !== "why" && (
        <button type="button" className="rec-link rec-lead__link" onClick={ops.toggleBreakdown}>
          Breakdown
        </button>
      )}
    </span>
  );
}

function timeNowText(record, person) {
  const zones = person && person.local_zone ? { ...record.zones, theirs: person.local_zone } : record.zones;
  return nowLine(zones, { now: new Date(), place: record.company && record.company.country });
}

/** "Dana’s time now: 07:20 in Boston (ET) · 14:20 yours", or nothing when unknown. */
export function TimeNow({ record, person = null }) {
  const line = timeNowText(record, person);
  if (!line) return null;
  return (
    <span className="rec-lead__time">
      <b>{firstName(person || record.contact)}’s time now:</b> {line}
    </span>
  );
}

/** Their words at reading size, line breaks kept, the quoted chain gone. */
export function Words({ text, size = "md", muted = false }) {
  const clean = readable(text);
  if (!clean) return null;
  return <div className={`rec-quote${size === "md" ? " rec-quote--md" : ""}${muted ? " rec-lead__muted" : ""}`}>{clean}</div>;
}

/** A Proposed thing, drawn only for its words: a dashed tag, never clickable. */
export function Proposed({ children = null }) {
  return (
    <span className="rec-proposed-wrap">
      {children}
      <span className="rec-proposed">Proposed</span>
    </span>
  );
}

/*
 * "in Next ↑": an owed row points at its Next line instead of carrying a second
 * button (the brief, "Who owns what"). It scrolls the record's own Next card into
 * view, inside whatever scrolls the record (the host's panel or the page).
 */
export function NextPointer() {
  const go = (e) => {
    const root = e.currentTarget.closest(".rec");
    const next = root && root.querySelector('section[aria-label="Next"]');
    if (next && next.scrollIntoView) next.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button type="button" className="rec-link rec-lead__pointer" onClick={go}>
      in Next ↑
    </button>
  );
}

/*
 * "Add": an empty field this block is the one home for, filled in place. Save is
 * black (the screen's one yellow is in Next); an error stays beside the control
 * that failed, with what was typed kept (the brief: "errors show next to the
 * control that failed"). Esc closes it as one layer, unless the cursor is still
 * in the field (escape.mjs never treats typing as a close).
 */
export function InlineAdd({ label = "Add", placeholder = "", multiline = false, hint = null, onSave }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cancel = () => {
    setOpen(false);
    setValue("");
    setError(null);
  };
  useEscapeLayer(open, cancel);
  if (!open) {
    return (
      <button type="button" className="rec-link rec-lead__link" onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }
  const save = async () => {
    if (!value.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(multiline ? value.replace(/\r\n/g, "\n").trim() : value.trim());
      cancel();
    } catch (e) {
      setError(e.message || "Couldn’t save that");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rec-lead__add">
      {multiline ? (
        <textarea className="rec-textarea" autoFocus rows={4} placeholder={placeholder} value={value} disabled={busy} onChange={(e) => setValue(e.target.value)} />
      ) : (
        <input
          className="rec-input"
          autoFocus
          placeholder={placeholder}
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
          }}
        />
      )}
      {hint && <span className="rec-meta">{hint}</span>}
      <span className="rec-actionsrow">
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy || !value.trim()} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={cancel}>
          Cancel
        </button>
        {error && <span className="rec-err">{error}</span>}
      </span>
    </div>
  );
}

/** "▸ What we quoted · pricing email Sep 22": a collapsed sub-section (frame 1d). */
export function Fold({ title, sub = null, open = false, onToggle, children }) {
  return (
    <>
      <button type="button" className="rec-fold rec-lead__fold" aria-expanded={open} onClick={onToggle}>
        <span className="rec-lead__chev" aria-hidden="true">{open ? "▾" : "▸"}</span>
        <span className="rec-strong">{title}</span>
        {sub && <span className="rec-sub rec-lead__foldsub">{sub}</span>}
      </button>
      {open && <div className="rec-lead__foldbody">{children}</div>}
    </>
  );
}

/*
 * New pricing a later email suggested, still waiting in Approvals (pendingQuote,
 * migration 065). The quote above it stays the one on record until it is
 * approved, so this says what is coming and where to answer it.
 */
export function PendingQuoteLine({ pending, d }) {
  if (!pending) return null;
  const whose = pending.by === "them" ? "their" : "our";
  return (
    <div className="rec-sub rec-lead__gap">
      New pricing waiting in Approvals: {pending.terms.join(" · ")}
      {pending.at ? ` (${whose} email, ${d(pending.at)})` : ""}
    </div>
  );
}
